// 📄 components/StepASNProcess.tsx
import React from 'react';
import { AlertTriangle, Info } from 'lucide-react';
import { AsnFormData } from '../../../../../services/commerce/integrationService';

interface StepASNProcessProps {
  processCode: string;
  functionModule: string;
  autoDeliveryTrigger: boolean;
  onChange: (field: keyof AsnFormData, value: any) => void;
  isInvalid: boolean;
}

export function StepASNProcess({ processCode, functionModule, autoDeliveryTrigger, onChange, isInvalid }: StepASNProcessProps) {
  return (
    <div className="space-y-4 animate-fadeIn">
      {/* Ruta SPRO / Transacción SAP */}
      <div className="bg-teal-500/5 border border-teal-500/10 rounded-xl p-3 space-y-1">
        <span className="text-[10px] font-bold text-teal-400 uppercase tracking-widest block">Ruta SPRO / Transacción SAP</span>
        <p className="font-mono text-xs text-gray-300">
          <span className="bg-zinc-800 px-1.5 py-0.5 rounded text-teal-300 font-bold mr-1.5">BD51 / WE42</span>
          <span>SAP Menu → Tools → ALE → ALE Development → IDocs → Inbound Processing → Maintain Process Codes</span>
        </p>
      </div>

      <div>
        <h3 className="text-lg font-medium text-teal-400">2. Process Code & Function Module (BD51 / WE42)</h3>
        <p className="text-sm text-gray-400 mt-1">
          Valida que el código de proceso esté registrado en <code className="bg-black/30 px-1 rounded text-teal-300">WE42</code>{' '}
          y asígnalo al módulo de función que procesará automáticamente el IDoc de entrega entrante en el ERP.
          Usa <code className="bg-black/30 px-1 rounded text-teal-300">BD51</code> para verificar el atributo de procesamiento.
        </p>
      </div>

      <div className="bg-white/5 p-4 rounded-xl border border-white/5 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1">
            Código de Proceso (Process Code) <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            value={processCode}
            onChange={(e) => onChange('processCode', e.target.value.toUpperCase())}
            placeholder="ej. DEDI"
            maxLength={4}
            className={`w-full bg-zinc-800 border rounded-lg p-2.5 text-sm font-mono text-white focus:outline-none focus:border-teal-500 ${
              isInvalid && !processCode ? 'border-red-500' : 'border-white/10'
            }`}
          />
          <p className="text-[10px] text-gray-500 mt-1 px-1">
            Estándar SAP para entregas entrantes: <code className="bg-black/30 px-0.5 rounded">DEDI</code>
          </p>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1">
            Módulo de Función (Function Module) <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            value={functionModule}
            onChange={(e) => onChange('functionModule', e.target.value.toUpperCase())}
            placeholder="ej. IDOC_INPUT_DESADV"
            className={`w-full bg-zinc-800 border rounded-lg p-2.5 text-sm font-mono text-white focus:outline-none focus:border-teal-500 ${
              isInvalid && !functionModule ? 'border-red-500' : 'border-white/10'
            }`}
          />
          <p className="text-[10px] text-gray-500 mt-1 px-1">
            Estándar: <code className="bg-black/30 px-0.5 rounded">IDOC_INPUT_DESADV</code>
          </p>
        </div>
      </div>

      {/* Toggle disparo automático */}
      <div className="bg-white/5 border border-white/5 rounded-xl p-4">
        <label className="flex items-center gap-4 cursor-pointer">
          <div className="relative">
            <input
              type="checkbox"
              checked={autoDeliveryTrigger}
              onChange={(e) => onChange('autoDeliveryTrigger', e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-10 h-6 bg-zinc-700 rounded-full peer peer-checked:after:translate-x-full peer-checked:bg-teal-500 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all" />
          </div>
          <div>
            <span className="text-sm font-medium text-white block">Disparo automático de creación de entrega</span>
            <span className="text-xs text-gray-400">
              Activa el indicador de procesamiento automático en BD51 para que el IDoc cree la entrega entrante
              sin intervención manual tras su recepción.
            </span>
          </div>
        </label>
      </div>

      {/* Tabla de referencia rápida */}
      <div className="bg-zinc-900/40 border border-white/5 rounded-xl p-4">
        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-3">Referencia Rápida — Process Codes Estándar</span>
        <div className="grid grid-cols-3 gap-2 text-xs">
          <div className="bg-zinc-800 rounded-lg p-2.5 text-center">
            <code className="text-teal-300 font-bold block">DEDI</code>
            <span className="text-gray-400 text-[10px]">Inbound Delivery (DESADV)</span>
          </div>
          <div className="bg-zinc-800 rounded-lg p-2.5 text-center">
            <code className="text-gray-500 font-bold block">DELO</code>
            <span className="text-gray-400 text-[10px]">Outbound Delivery</span>
          </div>
          <div className="bg-zinc-800 rounded-lg p-2.5 text-center">
            <code className="text-gray-500 font-bold block">BADI</code>
            <span className="text-gray-400 text-[10px]">BAdI personalizado</span>
          </div>
        </div>
      </div>

      <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-4 text-yellow-400 flex gap-3 text-sm">
        <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold">Verificar atributo de procesamiento en BD51</p>
          <p className="text-xs text-yellow-500/80 mt-0.5">
            En la transacción <code className="bg-black/30 px-1 rounded">BD51</code>, confirma que el módulo de función{' '}
            <code className="bg-black/30 px-1 rounded">IDOC_INPUT_DESADV</code> tiene el indicador de procesamiento
            correcto (generalmente <strong>1 - Llamada inmediata</strong>) para garantizar el disparo en tiempo real.
          </p>
        </div>
      </div>
    </div>
  );
}
